import { CloseOutlined } from '@ant-design/icons';
import { ProLayout } from '@ant-design/pro-components';
import { App, Button, Grid, theme } from 'antd';
import { memo, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useGuardedNavigation } from '@/app/NavigationFeedback';
import { ErrorBoundary, Footer } from '@/components';
import { PressRipple } from '@/components/PressRipple';
import {
  appInfo,
  defaultRoute,
  filterMenuItems,
  flattenRoutes,
  menuItems,
  pageItems,
} from '@/config/app';
import { localizeMenuItems } from '@/config/routeLabels';
import { useResolvedTheme } from '@/hooks/useResolvedTheme';
import { useShellText } from '@/hooks/useShellText';
import { useAuthStore } from '@/stores/authStore';
import { useLayoutSettingsStore } from '@/stores/layoutSettingsStore';
import { useLocaleStore } from '@/stores/localeStore';
import { useTabStore } from '@/stores/tabStore';
import { HeaderActions } from './HeaderActions';
import { HeaderBreadcrumb } from './HeaderBreadcrumb';
import { NavigationMenu } from './NavigationMenu';
import { resolveNavigation } from './navigationModel';
import { PreferencesDrawer } from './PreferencesDrawer';
import { ShellHeader } from './ShellHeader';
import {
  deferResolvedOpenKeys,
  findMenuKeyPath,
  resolveResponsiveNavigation,
  shouldBeginRouteSelection,
} from './sidebarNavigation';
import { useAppLayoutStyles } from './styles';
import TabWorkspace from './TabWorkspace';

const SIDEBAR_WIDTH = 232;
const COLLAPSED_SIDEBAR_WIDTH = 80;

function MobileRouteClose({
  path,
  onCollapse,
}: {
  path: string;
  onCollapse?: (collapsed: boolean) => void;
}) {
  const previousPath = useRef(path);
  useEffect(() => {
    if (previousPath.current !== path) {
      previousPath.current = path;
      onCollapse?.(true);
    }
  }, [path, onCollapse]);
  return null;
}

const ResponsiveAppLayout = memo(function ResponsiveAppLayout({
  isPhone,
  isCompact,
  disableMobile,
}: {
  isPhone: boolean;
  isCompact: boolean;
  disableMobile: boolean;
}) {
  const { styles } = useAppLayoutStyles();
  const { token } = theme.useToken();
  const t = useShellText();
  const { message } = App.useApp();
  const locale = useLocaleStore((state) => state.locale);
  const dark = useResolvedTheme();
  const location = useLocation();
  const guard = useGuardedNavigation();
  const openTab = useTabStore((state) => state.openTab);
  const currentUser = useAuthStore((state) => state.currentUser);
  const logout = useAuthStore((state) => state.logout);
  const settings = useLayoutSettingsStore((state) => state.settings);
  const [desktopCollapsed, setDesktopCollapsed] = useState(false);
  const [compactExpanded, setCompactExpanded] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [openKeys, setOpenKeys] = useState<string[]>([]);
  const [pendingKey, setPendingKey] = useState<string>();
  const [preferencesOpen, setPreferencesOpen] = useState(false);
  const routeSelectionPendingRef = useRef(false);
  const pendingMenuKeysRef = useRef<string[] | undefined>(undefined);
  const isDesktop = !isPhone && !isCompact;
  const navigationMode = settings.navigationMode ?? 'side';
  const isMixedNavigation = !isPhone && navigationMode === 'mixed';
  const menuType = settings.menuType ?? 'single';
  const collapsed = isPhone
    ? !mobileMenuOpen
    : isCompact
      ? !compactExpanded
      : desktopCollapsed;
  const access = currentUser?.access === 'admin' ? 'canAdmin' : undefined;
  const permissions = currentUser?.permissions ?? [];
  const isSuperAdmin = currentUser?.isSuperAdmin === true;
  const visibleMenuItems = useMemo(
    () =>
      localizeMenuItems(
        filterMenuItems(menuItems, access, permissions, isSuperAdmin),
        locale,
      ),
    [access, isSuperAdmin, locale, permissions],
  );
  const visibleRoutes = useMemo(
    () =>
      flattenRoutes(
        localizeMenuItems(
          filterMenuItems(pageItems, access, permissions, isSuperAdmin),
          locale,
        ),
      ),
    [access, isSuperAdmin, locale, permissions],
  );
  const routeKeyPath = useMemo(
    () => findMenuKeyPath(visibleMenuItems, location.pathname),
    [visibleMenuItems, location.pathname],
  );
  const routeOpenKeys = useMemo(() => routeKeyPath.slice(0, -1), [routeKeyPath]);
  const navigation = useMemo(
    () =>
      resolveNavigation(visibleMenuItems, location.pathname, navigationMode, !isPhone),
    [visibleMenuItems, location.pathname, navigationMode, isPhone],
  );
  const currentPath = `${location.pathname}${location.search}${location.hash}`;
  const currentRoute = visibleRoutes.find((item) => item.path === location.pathname);
  const selectedKeys = pendingKey ? [pendingKey] : routeKeyPath.slice(-1);
  const splitSidebarMode =
    isDesktop &&
    navigationMode === 'side' &&
    (menuType === 'twoColumn' || menuType === 'splitServiceGrid');
  const splitSidebar = splitSidebarMode && !collapsed;
  const serviceGrid =
    isDesktop &&
    !collapsed &&
    (menuType === 'serviceGrid' || menuType === 'splitServiceGrid');
  const showSidebar =
    isPhone ||
    navigationMode === 'side' ||
    (isMixedNavigation && navigation.sidebar.length > 0);
  const siderWidth = splitSidebar
    ? SIDEBAR_WIDTH + COLLAPSED_SIDEBAR_WIDTH
    : SIDEBAR_WIDTH;
  const rootKeys = visibleMenuItems.map((item) => item.key);

  useEffect(() => {
    if (!currentRoute) return;
    openTab({
      id: currentRoute.id,
      label: currentRoute.label,
      path: currentPath,
      closable: currentRoute.closable,
    });
    document.title = `${currentRoute.label} - ${appInfo.name}`;
  }, [currentPath, currentRoute, openTab]);

  useLayoutEffect(() => {
    // Release the navigation guard before the committed page accepts menu clicks.
    if (!currentPath) return;
    routeSelectionPendingRef.current = false;
    setPendingKey(undefined);
    setOpenKeys(pendingMenuKeysRef.current ?? routeOpenKeys);
    pendingMenuKeysRef.current = undefined;
  }, [currentPath, routeOpenKeys]);

  function handleCollapse(nextCollapsed: boolean) {
    if (isPhone) {
      if (!nextCollapsed) setOpenKeys(routeOpenKeys);
      setMobileMenuOpen(!nextCollapsed);
    } else if (isCompact) setCompactExpanded(!nextCollapsed);
    else setDesktopCollapsed(nextCollapsed);
  }
  function handleOpenChange(nextOpenKeys: string[]) {
    deferResolvedOpenKeys({
      nextOpenKeys,
      getCurrentOpenKeys: () => openKeys,
      rootKeys,
      // A pending page must not swallow an explicit submenu interaction.
      routeSelectionPending: () => false,
      onResolved: (keys) => {
        if (routeSelectionPendingRef.current) pendingMenuKeysRef.current = keys;
        setOpenKeys(keys);
      },
    });
  }
  function openRoute(path: string, onApproved?: () => void) {
    const target = visibleRoutes.find((item) => item.path === path);
    if (!target) {
      return;
    }
    void guard((navigate) => {
      const pending = shouldBeginRouteSelection(path, currentPath);
      pendingMenuKeysRef.current = undefined;
      routeSelectionPendingRef.current = pending;
      setPendingKey(pending ? target.id : undefined);
      onApproved?.();
      navigate(path);
    });
  }
  async function handleLogout() {
    await guard(async (navigate) => {
      try {
        await logout();
      } catch {
        void message.warning(
          t(
            '服务端退出失败，本地登录信息已清除。',
            'Server sign-out failed; local session cleared.',
          ),
        );
      } finally {
        navigate('/user/login', { replace: true });
      }
    });
  }

  const actions = (
    <HeaderActions
      isPhone={isPhone}
      menuItems={filterMenuItems(menuItems, access, permissions, isSuperAdmin)}
      onNavigate={openRoute}
      onPreferences={() => setPreferencesOpen(true)}
      preferencesOpen={preferencesOpen}
      onLogout={handleLogout}
    />
  );

  return (
    <ProLayout
      ErrorBoundary={ErrorBoundary}
      className={styles.layoutRoot}
      style={{ height: isPhone ? 'auto' : '100%', minHeight: isPhone ? '100dvh' : 0 }}
      title={appInfo.name}
      logo={false}
      layout={!isPhone && navigationMode === 'top' ? 'top' : 'mix'}
      navTheme={dark ? 'realDark' : 'light'}
      contentWidth="Fluid"
      fixedHeader
      fixSiderbar
      breakpoint="lg"
      disableMobile={disableMobile}
      collapsed={collapsed}
      onCollapse={handleCollapse}
      route={{ path: '/', routes: visibleMenuItems }}
      location={{ pathname: location.pathname }}
      contentStyle={{
        padding: 0,
        flex: isPhone ? '1 1 auto' : '1 1 0',
        minHeight: 0,
        overflow: isPhone ? 'visible' : 'hidden',
      }}
      menu={{ collapsedWidth: COLLAPSED_SIDEBAR_WIDTH }}
      menuRender={showSidebar ? undefined : false}
      splitMenus={false}
      siderWidth={siderWidth}
      menuHeaderRender={false}
      collapsedButtonRender={false}
      headerRender={(headerProps) => (
        <>
          {isPhone && (
            <MobileRouteClose path={currentPath} onCollapse={headerProps.onCollapse} />
          )}
          <ShellHeader
            compactBrand={splitSidebarMode}
            brandWidth={
              splitSidebar || (showSidebar && collapsed)
                ? COLLAPSED_SIDEBAR_WIDTH
                : siderWidth
            }
            secondaryTitle={splitSidebar ? appInfo.name : undefined}
            collapsed={collapsed}
            isPhone={isPhone}
            showSidebar={showSidebar}
            onCollapse={() => headerProps.onCollapse?.(!collapsed)}
            onHome={() => openRoute(defaultRoute.path)}
            actions={actions}
            leading={
              navigation.top.length ? (
                <nav aria-label={t('顶部导航', 'Top navigation')}>
                  <NavigationMenu
                    mode="horizontal"
                    items={navigation.top}
                    selectedKeys={
                      isMixedNavigation
                        ? navigation.currentRoot
                          ? [navigation.currentRoot.key]
                          : []
                        : selectedKeys
                    }
                    onNavigate={openRoute}
                  />
                </nav>
              ) : (
                <HeaderBreadcrumb
                  items={visibleMenuItems}
                  pathname={location.pathname}
                  currentLabel={currentRoute?.label}
                  isPhone={isPhone}
                />
              )
            }
          />
        </>
      )}
      menuContentRender={(siderProps) => (
        <nav
          aria-label={t('侧栏导航', 'Sidebar navigation')}
          className={styles.sidebarMenuSurface}
        >
          {isPhone && (
            <div
              className={styles.sidebarBrand}
              style={{
                paddingInline: 16,
                justifyContent: 'flex-start',
                gap: 8,
                borderBottom: `1px solid ${token.colorBorderSecondary}`,
              }}
            >
              <PressRipple targetSelector="button[data-press-ripple-target]">
                <Button
                  data-press-ripple-target
                  type="text"
                  aria-label={t('关闭导航', 'Close navigation')}
                  icon={<CloseOutlined />}
                  onClick={() => siderProps.onCollapse?.(true)}
                  style={{ width: 36, height: 36, flexShrink: 0 }}
                />
              </PressRipple>
              <strong>{appInfo.name}</strong>
            </div>
          )}
          {splitSidebar ? (
            <div style={{ display: 'flex', minHeight: '100%' }}>
              <div
                style={{
                  flex: '0 0 80px',
                  width: 80,
                  borderInlineEnd: `1px solid ${token.colorBorderSecondary}`,
                }}
              >
                <NavigationMenu
                  items={navigation.roots}
                  selectedKeys={
                    navigation.currentRoot ? [navigation.currentRoot.key] : []
                  }
                  onNavigate={openRoute}
                  collapsed
                />
              </div>
              <div style={{ flex: '0 0 232px', width: 232, minWidth: 0 }}>
                <NavigationMenu
                  items={
                    navigation.secondary.length
                      ? navigation.secondary
                      : navigation.currentRoot?.path
                        ? [navigation.currentRoot]
                        : []
                  }
                  selectedKeys={selectedKeys}
                  openKeys={openKeys.filter(
                    (key) => key !== navigation.currentRoot?.key,
                  )}
                  onOpenChange={(keys) =>
                    handleOpenChange(
                      navigation.currentRoot
                        ? [navigation.currentRoot.key, ...keys]
                        : keys,
                    )
                  }
                  onNavigate={openRoute}
                  serviceGrid={serviceGrid}
                  rootGrid
                />
              </div>
            </div>
          ) : (
            <NavigationMenu
              items={navigation.sidebar}
              selectedKeys={selectedKeys}
              openKeys={
                isMixedNavigation
                  ? openKeys.filter((key) => key !== navigation.currentRoot?.key)
                  : openKeys
              }
              onOpenChange={(keys) =>
                handleOpenChange(
                  isMixedNavigation && navigation.currentRoot
                    ? [navigation.currentRoot.key, ...keys]
                    : keys,
                )
              }
              onNavigate={(path) => {
                openRoute(path, () => {
                  // 当前页点击也在确认后关闭手机抽屉。
                  if (isPhone) {
                    siderProps.onCollapse?.(true);
                  }
                });
              }}
              collapsed={collapsed && !isPhone}
              serviceGrid={serviceGrid}
              rootGrid={navigationMode === 'mixed'}
            />
          )}
        </nav>
      )}
      footerRender={false}
    >
      <TabWorkspace
        isMobile={isPhone}
        siderWidth={
          showSidebar && !isPhone
            ? collapsed
              ? COLLAPSED_SIDEBAR_WIDTH
              : siderWidth
            : 0
        }
        footer={settings.footerRender === false ? null : <Footer />}
      />
      <PreferencesDrawer
        open={preferencesOpen}
        onClose={() => setPreferencesOpen(false)}
        isPhone={isPhone}
      />
    </ProLayout>
  );
});

export default function AppLayout() {
  const screens = Grid.useBreakpoint();
  const { token } = theme.useToken();
  // Grid's first snapshot is empty. Read the same media queries immediately
  // so tablets never mount the phone shell before the subscription is ready.
  const { isPhone, isCompact, disableMobile } = resolveResponsiveNavigation({
    md:
      screens.md ??
      (typeof window !== 'undefined' &&
        window.matchMedia(`(min-width: ${token.screenMD}px)`).matches),
    lg:
      screens.lg ??
      (typeof window !== 'undefined' &&
        window.matchMedia(`(min-width: ${token.screenLG}px)`).matches),
  });
  // xl/xxl changes do not change our phone/compact/desktop navigation. Keep
  // those notifications out of the shell and its page providers.
  return (
    <ResponsiveAppLayout
      isPhone={isPhone}
      isCompact={isCompact}
      disableMobile={disableMobile}
    />
  );
}
